export function generateTermsFromTemplate({
  formData,
  templateFields,
  startDate,
  endDate,
  redemptionLocationTypeStep4,
  selectAllRedemptionLocations,
  selectedRedemptionBranches,
  categorySpecificTerms,
  selectAllFreeDeliveryLocations,
}) {
    let redemptionLocations = "";

    if (
      formData.redemption_type &&
      formData.redemption_type.includes("In-store")
    ) {
      if (redemptionLocationTypeStep4 === "region") {
        redemptionLocations = selectAllRedemptionLocations
          ? "Nationwide"
          : formData.redemption_region_location
              .filter((loc) => loc.region && loc.location)
              .map(
                (loc) =>
                  `${loc.region}-${
                    loc.location === "other" ? loc.customLocation : loc.location
                  }`
              )
              .join(", ") || "No locations selected";
      } else if (redemptionLocationTypeStep4 === "branch") {
        if (selectedRedemptionBranches.length === 0) {
          redemptionLocations = "No branches selected";
        } else {
          redemptionLocations = selectedRedemptionBranches
            .map((branch) => {
              let locationInfo = `${branch.name} (${branch.location}, ${branch.region})`;
              if (branch.google_url) {
                locationInfo += ` - Google Maps: ${branch.google_url}`;
              }
              return locationInfo;
            })
            .join("; ");
        }
      } else if (redemptionLocationTypeStep4 === "partner") {
        const partnersWithBranches = formData.redemption_partner_location.filter(
          (p) => p.branches && p.branches.length > 0
        );
        if (partnersWithBranches.length === 0) {
          redemptionLocations = "No partners selected";
        } else {
          redemptionLocations = partnersWithBranches
            .map((partner) => {
              const partnerBranches = partner.branches || [];
              const branchInfo = partnerBranches
                .map((branch) => {
                  let branchLocation = `${branch.name} (${branch.location}, ${branch.region})`;
                  if (branch.google_url) {
                    branchLocation += ` - Google Maps: ${branch.google_url}`;
                  }
                  return branchLocation;
                })
                .join(", ");
              return `${partner.partner_name}: ${branchInfo}`;
            })
            .join("; ");
        }
      }
    }

    // Add online redemption if selected
    if (
      formData.redemption_type &&
      formData.redemption_type.includes("Online")
    ) {
      const onlineInfo = formData.online_link
        ? `Online Redemption: ${formData.online_link}`
        : "Online Redemption: Available";
      if (redemptionLocations) {
        redemptionLocations += `\n${onlineInfo}`;
      } else {
        redemptionLocations = onlineInfo;
      }
    }

    if (!redemptionLocations) {
      redemptionLocations = "No redemption locations specified";
    }

    // Determine redemption types selected
    const redemptionTypes = formData.redemption_type || [];
    const hasInStore = redemptionTypes.includes("In-store");
    const hasOnline = redemptionTypes.includes("Online");

    let terms = `TERMS AND CONDITIONS

Vendor Information:
Brand Name: ${templateFields.vendor || "[BRAND NAME]"}
Platform: DealBoxx
Product listed on https://dealboxx.promo

Deal Validity:
${
  formData.validity_type === "flash_sale"
    ? (() => {
        const duration = formData.flash_duration || 0;
        const endTime = formData.flash_end_time;
        const startTime = formData.flash_start_time;

        // Check if we have valid start time and duration
        if (startTime && duration > 0) {
          const startDate = new Date(startTime);
          const endDate = new Date(
            startDate.getTime() + duration * 60 * 60 * 1000
          );

          // Validate dates
          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            const startDateStr = startDate.toISOString().split("T")[0];
            const startTimeStr = startDate
              .toISOString()
              .split("T")[1]
              .split(".")[0]
              .substring(0, 5);
            const endDateStr = endDate.toISOString().split("T")[0];
            const endTimeStr = endDate
              .toISOString()
              .split("T")[1]
              .split(".")[0]
              .substring(0, 5);

            return `Flash Sale Duration: ${duration} hours
Start Date & Time: ${startDateStr} at ${startTimeStr}
End Date & Time: ${endDateStr} at ${endTimeStr}`;
          }
        }

        // Fallback for invalid or missing data
        return `Flash Sale Duration: ${
          duration ? `${duration} hours` : "[Duration]"
        }
Start Date & Time: ${startTime ? startTime.split("T")[0] : "[Start date]"} at ${
          startTime
            ? startTime.split("T")[1]?.split(".")[0].substring(0, 5)
            : "[Start time]"
        }
End Date & Time: ${endTime ? endTime.split("T")[0] : "[End date]"} at ${
          endTime
            ? endTime.split("T")[1]?.split(".")[0].substring(0, 5)
            : "[End time]"
        }`;
      })()
    : `Start Date: ${
        templateFields.validityStart || startDate || "[Start date]"
      }
End Date: ${templateFields.validityEnd || endDate || "[End date]"}`
}

Redemption Location:
${redemptionLocations || "[Branches/Online link]"}

Offer Details:
${(() => {
  let offerDetails = "";

  // Offer Type and Pricing
  if (formData.offer_type === "discount") {
    offerDetails += `Discount Offer:
Original Price: GHS ${formData.discount_original_price || "[Original Price]"}
Discounted Price: GHS ${formData.discount_sale_price || "[Sale Price]"}
Savings: GHS ${
      formData.discount_original_price - formData.discount_sale_price ||
      "[Savings]"
    }`;
  } else if (formData.offer_type === "bundle") {
    offerDetails += `Bundle Offer:
Original Sum: GHS ${formData.original_sum || "[Original Sum]"}
Bundle Price: GHS ${formData.bundle_price || "[Bundle Price]"}
Total Savings: GHS ${formData.bundle_savings || "[Savings]"}`;
  } else if (formData.offer_type === "bogo") {
    offerDetails += `Buy One Get One (BOGO) Offer:
Item Price: GHS ${formData.bogo_item_price || "[Item Price]"}
Buy Quantity: ${formData.bogo_quantity || "[Buy Qty]"}
Get Free Quantity: ${formData.bogo_free_quantity || "[Free Qty]"}`;
  } else {
    offerDetails += `${formData.offer_type || "[Offer type]"}`;
  }

  return offerDetails;
})()}

${
  formData.redemption_limits.length > 0
    ? `Redemption Limits:
${formData.redemption_limits
  .filter((limit) => limit.trim())
  .map((limit) => `• ${limit}`)
  .join("\n")}`
    : ""
}

${
  formData.exclusions.length > 0
    ? `Exclusions:
${formData.exclusions
  .filter((exclusion) => exclusion.trim())
  .map((exclusion) => `• ${exclusion}`)
  .join("\n")}`
    : ""
}

Redemption Process:
${
  templateFields.redemptionProcess
    ? templateFields.redemptionProcess
        .split("\n")
        .map((line) => {
          const trimmed = line.trim();
          if (!trimmed) return "";
          // Convert numbered lists and bullet points to proper format
          if (trimmed.match(/^\d+\./)) {
            return trimmed; // Keep numbered lists as is
          } else if (trimmed.startsWith("-") || trimmed.startsWith("•")) {
            return trimmed; // Keep bullet points as is
          } else if (trimmed.endsWith(":")) {
            return trimmed; // Keep headers as is
          } else {
            return trimmed; // Keep other content as is
          }
        })
        .filter((line) => line)
        .join("\n")
    : (() => {
        let processText = "";

        if (hasInStore) {
          processText += `
In-Store Redemption:
Available at: ${
            redemptionLocationTypeStep4 === "branch"
              ? selectedRedemptionBranches.length > 0
                ? selectedRedemptionBranches
                    .map(
                      (branch) =>
                        `${branch.name} (${branch.location}, ${branch.region})`
                    )
                    .join(", ")
                : "Selected branches"
              : redemptionLocationTypeStep4 === "partner"
              ? formData.redemption_partner_location.some(p => p.branches && p.branches.length > 0)
                ? formData.redemption_partner_location
                    .filter(p => p.branches && p.branches.length > 0)
                    .map((partner) => `${partner.partner_name} branches`)
                    .join(", ")
                : "Selected partner locations"
              : redemptionLocationTypeStep4 === "region"
              ? selectAllRedemptionLocations
                ? "All locations nationwide"
                : formData.redemption_region_location
                    .filter((loc) => loc.region && loc.location)
                    .map(
                      (loc) =>
                        `${loc.region}-${
                          loc.location === "other"
                            ? loc.customLocation
                            : loc.location
                        }`
                    )
                    .join(", ")
              : "Listed redemption points"
          }

Process: 
1. Present your DealBoxx redemption code and registered phone number
2. Show valid ID for verification
3. Complete purchase transaction
4. Receive receipt/order confirmation
5. Deal is redeemed upon successful payment

Important Notes:
- Redemption code is valid for one-time use only
- Must be redeemed within deal validity period
- Cannot be combined with other offers unless specified
- Refunds subject to vendor's refund policy`;
        }

        if (hasOnline) {
          processText += `
Online Redemption:
Process: 
1. Visit the brand's official website or mobile app
2. Add desired items to cart
3. Enter DealBoxx redemption code at checkout
4. Provide registered phone number for verification
5. Complete payment transaction
6. Receive order confirmation and tracking details

Important Notes:
- Redemption code is valid for one-time use only
- Must be redeemed within deal validity period
- Cannot be combined with other offers unless specified
- Delivery fees may apply (see delivery policy)
- Refunds subject to vendor's refund policy`;
        }

        if (!hasInStore && !hasOnline) {
          processText = `
In-Store Redemption:
Available at: ${
            redemptionLocationTypeStep4 === "branch"
              ? selectedRedemptionBranches.length > 0
                ? selectedRedemptionBranches
                    .map(
                      (branch) =>
                        `${branch.name} (${branch.location}, ${branch.region})`
                    )
                    .join(", ")
                : "Selected branches"
              : redemptionLocationTypeStep4 === "partner"
              ? formData.redemption_partner_location.some(p => p.branches && p.branches.length > 0)
                ? formData.redemption_partner_location
                    .filter(p => p.branches && p.branches.length > 0)
                    .map((partner) => `${partner.partner_name} branches`)
                    .join(", ")
                : "Selected partner locations"
              : redemptionLocationTypeStep4 === "region"
              ? selectAllRedemptionLocations
                ? "All locations nationwide"
                : formData.redemption_region_location
                    .filter((loc) => loc.region && loc.location)
                    .map(
                      (loc) =>
                        `${loc.region}-${
                          loc.location === "other"
                            ? loc.customLocation
                            : loc.location
                        }`
                    )
                    .join(", ")
              : "Listed redemption points"
          }

Process: 
1. Present your DealBoxx redemption code and registered phone number
2. Show valid ID for verification
3. Complete purchase transaction
4. Receive receipt/order confirmation
5. Deal is redeemed upon successful payment

Online Redemption:
Process: 
1. Visit the brand's official website or mobile app
2. Add desired items to cart
3. Enter DealBoxx redemption code at checkout
4. Provide registered phone number for verification
5. Complete payment transaction
6. Receive order confirmation and tracking details

Important Notes:
- Redemption code is valid for one-time use only
- Must be redeemed within deal validity period
- Cannot be combined with other offers unless specified
- Delivery fees may apply for online orders (see delivery policy)
- Refunds subject to vendor's refund policy`;
        }

        return processText || "Redemption process will be specified";
      })()
}`;

    terms += `

Terms and Limitations:
• Availability: While stocks last
• Redemption Limit: Maximum ${(templateFields.redemptionLimit || formData.redemption_limits?.[0] || "1 redemption/customer")} (per phone/email/device)
• Cannot be combined with other promotions unless stated`;

    // Add refund policy if available
    if (formData.refund_policy && formData.refund_policy.trim()) {
      terms += `

Refund Policy:
${formData.refund_policy}`;
    }

    terms += `

Misuse and Fraud:
We reserve the right to cancel redemptions that appear abusive or in breach of these terms.

Privacy and Data:
${
  templateFields.privacyLink
    ? `We process your data to fulfil the order. See our privacy policy: ${templateFields.privacyLink}.`
    : "We process your data to fulfil the order as per applicable privacy laws."
}

Governing Law:
These terms are governed by Ghana law.

Category Specific Terms:`;

    // Add category-specific terms based on selected categories
    if (formData.category.includes("Electronics & Gadgets")) {
      const warrantyPeriod = categorySpecificTerms.warrantyPeriod === "custom" 
        ? categorySpecificTerms.warrantyPeriodCustom 
        : categorySpecificTerms.warrantyPeriod === "not_applicable"
        ? "Not Applicable"
        : categorySpecificTerms.warrantyPeriod;
      
      const doaPeriod = categorySpecificTerms.doaReplacementDays === "not_applicable"
        ? "Not Applicable"
        : categorySpecificTerms.doaReplacementDays;
      
      terms += `\n\nElectronics & Gadgets:`;
      if (warrantyPeriod !== "Not Applicable") {
        terms += `\n• Minimum ${warrantyPeriod}-month warranty (manufacturer or seller)`;
      } else {
        terms += `\n• Warranty: Not Applicable`;
      }
      if (doaPeriod !== "Not Applicable") {
        terms += `\n• DOA (Defect on Arrival) replacement within ${doaPeriod} days of purchase`;
      } else {
        terms += `\n• DOA (Defect on Arrival) replacement: Not Applicable`;
      }
      terms += `\n• Warranty excludes accidental damage or misuse`;
    }

    if (formData.category.includes("Groceries")) {
      terms += `\n\nGroceries:
• Returns allowed only for damaged/expired items within ${categorySpecificTerms.returnWindow} hours
• Perishables (fresh produce, dairy, frozen foods) are final sale
• Store credit/refund applies for verified quality issues`;
    }

    if (formData.category.includes("Fashion & Accessories")) {
      terms += `\n\nFashion & Accessories:
• Returns within ${categorySpecificTerms.fashionReturnDays} days, must be unworn, unwashed, and with tags
• Hygiene items (e.g., underwear, earrings, swimwear) are final sale
• Customized or altered items are non-returnable`;
    }

    if (formData.category.includes("Real Estate & Housing")) {
      terms += `\n\nReal Estate & Housing:
• Security deposit required per tenancy agreement
• Advance notice ${categorySpecificTerms.advanceNoticeDays} days required for lease termination
• Maintenance of common areas covered by landlord/agency
• Buyer/tenant responsible for utility payments unless stated otherwise`;
    }

    if (formData.category.includes("Furniture")) {
      const furnitureWarrantyPeriod = categorySpecificTerms.furnitureWarrantyPeriod === "custom" 
        ? categorySpecificTerms.furnitureWarrantyPeriodCustom 
        : categorySpecificTerms.furnitureWarrantyPeriod === "not_applicable"
        ? "Not Applicable"
        : categorySpecificTerms.furnitureWarrantyPeriod;
      
      terms += `\n\nFurniture:`;
      if (furnitureWarrantyPeriod !== "Not Applicable") {
        terms += `\n• Warranty on structural/frame issues: ${furnitureWarrantyPeriod} months`;
      } else {
        terms += `\n• Warranty on structural/frame issues: Not Applicable`;
      }
      terms += `\n• Returns within ${categorySpecificTerms.furnitureReturnDays} days for manufacturing defects only
• Assembly/installation fees non-refundable
• Customized furniture is final sale`;
    }

    if (formData.category.includes("Beauty, Spas & Wellness")) {
      terms += `\n\nBeauty, Spas & Wellness:
• Appointments may be rescheduled with ${categorySpecificTerms.rescheduleNoticeHours}h notice
• Prepaid packages valid for ${categorySpecificTerms.packageValidityMonths} months
• Services performed are non-refundable
• Product sales follow general beauty product hygiene rules (no returns if opened/used)`;
    }

    if (formData.category.includes("Baby & Mother Care")) {
      terms += `\n\nBaby & Mother Care:
• Returns within ${categorySpecificTerms.babyReturnDays} days, unopened and in original packaging
• Hygiene-related products (feeding bottles, diapers, breast pumps) are final sale
• Warranty for strollers, car seats, and electronic devices: ${categorySpecificTerms.babyWarrantyPeriod} months`;
    }

    if (formData.category.includes("Tourism & Travel")) {
      terms += `\n\nTourism & Travel:
• Booking changes/cancellations subject to provider terms
• Rescheduling requires ${categorySpecificTerms.travelNoticeHours}h notice before travel
• Non-refundable for no-shows
• Packages exclude visa, insurance, and personal expenses unless specified`;
    }

    if (formData.category.includes("Sports")) {
      terms += `\n\nSports:
• Returns within ${categorySpecificTerms.sportsReturnDays} days, unused and with original packaging
• Protective gear and hygiene-related items (helmets, mouth-guards) are final sale
• Equipment warranty: ${categorySpecificTerms.sportsWarrantyPeriod} months, depending on item`;
    }

    if (formData.category.includes("Automotive")) {
      const autoWarrantyPeriod = categorySpecificTerms.autoWarrantyPeriod === "custom" 
        ? categorySpecificTerms.autoWarrantyPeriodCustom 
        : categorySpecificTerms.autoWarrantyPeriod === "not_applicable"
        ? "Not Applicable"
        : categorySpecificTerms.autoWarrantyPeriod;
      
      terms += `\n\nAutomotive:`;
      if (autoWarrantyPeriod !== "Not Applicable") {
        terms += `\n• Warranty on parts/accessories: ${autoWarrantyPeriod} months`;
      } else {
        terms += `\n• Warranty on parts/accessories: Not Applicable`;
      }
      terms += `\n• Returns within ${categorySpecificTerms.autoReturnDays} days for defective items (unused)
• Installed parts/services are non-refundable
• Vehicle sales final, except under specific dealership guarantees`;
    }

    if (formData.category.includes("Events and Entertainment")) {
      terms += `\n\nEvents and Entertainment:
• Tickets are non-refundable, unless event is canceled
• Rescheduling subject to event organizer policy
• Name transfers allowed up to ${categorySpecificTerms.nameTransferHours}h before the event (if permitted)
• Merchandise follows general returns policy`;
    }

    if (formData.category.includes("Restaurants & Eateries")) {
      terms += `\n\nRestaurants & Eateries:
• Valid for dine-in/takeaway/delivery
• Service charges & delivery fees excluded from discounts
• No refunds once food/drinks are served or delivered
• Special offers cannot be combined with other promotions`;
    }

    if (formData.category.includes("Stationery")) {
      terms += `\n\nStationery:
• Returns within ${categorySpecificTerms.stationeryReturnDays} days, unused and in original packaging
• Customized/printed stationery is final sale
• Defective items replaced or refunded upon verification
• Warranty not applicable for consumables (pens, markers, etc.)`;
    }

    // Add delivery information
    if (formData.delivery_fee === "not_apply") {
      terms += `\n\nDelivery Information:
• No delivery fees apply to this deal
• Standard delivery terms and conditions apply`;
    } else if (formData.delivery_fee === "apply") {
      terms += `\n\nDelivery Information:
• Standard delivery fees apply
• Delivery fees are calculated based on location and weight
• Delivery terms and conditions apply`;
    } else if (formData.delivery_fee === "free_within_location") {
      if (selectAllFreeDeliveryLocations) {
        terms += `\n\nDelivery Information:
• Free delivery nationwide
• Standard delivery terms and conditions apply`;
      } else if (formData.free_delivery_areas.length > 0) {
        const freeDeliveryAreas = formData.free_delivery_areas
          .filter((area) => area.region && area.location)
          .map((area) => `${area.region} - ${area.location}`)
          .join(", ");
        terms += `\n\nDelivery Information:
• Free delivery available in: ${freeDeliveryAreas}
• Standard delivery fees apply to other areas
• Delivery terms and conditions apply`;
      } else {
        terms += `\n\nDelivery Information:
• Free delivery areas to be specified
• Standard delivery fees apply to other areas
• Delivery terms and conditions apply`;
      }
    }

    return terms;
}
